import React from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import { tokens } from '../../theme';
import { formatGerman } from '../../helpers/chartUtils';

export interface CoregMatrixCounts {
  both_new: number;
  mc_new_vnr_dup: number;
  mc_dup_vnr_new: number;
  both_dup: number;
}

interface CoregMatrixWidgetProps {
  counts: CoregMatrixCounts;
}

const pct = (value: number, total: number): string => {
  if (total <= 0) return '0,0 %';
  return `${(100 * (value / total)).toLocaleString('de-DE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`;
};

const CoregMatrixWidget: React.FC<CoregMatrixWidgetProps> = ({ counts }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const total = counts.both_new + counts.mc_new_vnr_dup + counts.mc_dup_vnr_new + counts.both_dup;

  const cell = (label: string, value: number, tone: 'green' | 'amber' | 'red', hint: string) => {
    const bg =
      tone === 'green'
        ? colors.greenAccent[700]
        : tone === 'red'
          ? colors.redAccent[700]
          : '#b08900';
    return (
      <Box
        sx={{
          backgroundColor: bg,
          color: '#ffffff',
          p: 2.5,
          borderRadius: 1,
          minHeight: 120,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" sx={{ opacity: 0.85 }}>
          {hint}
        </Typography>
        <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>
          {formatGerman.format(value)}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.85 }}>
          {pct(value, total)}
        </Typography>
      </Box>
    );
  };

  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: '10px',
        backgroundColor: colors.primary[400],
      }}
    >
      <Typography variant="h6" fontWeight="bold" mb={2}>
        2×2 Matrix — Mailchimp × VNR (NSS) Response
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '140px 1fr 1fr',
          gridTemplateRows: 'auto 1fr 1fr',
          gap: 1,
        }}
      >
        <Box />
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Mailchimp: Neu
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Mailchimp: Dublette
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            pr: 1.5,
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            VNR: Neu
          </Typography>
        </Box>
        {cell('Beide neu', counts.both_new, 'green', 'voll verwertet')}
        {cell('Nur MC-Dublette', counts.mc_dup_vnr_new, 'amber', 'halb verwertet (VNR profitiert)')}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            pr: 1.5,
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            VNR: Dublette
          </Typography>
        </Box>
        {cell('Nur VNR-Dublette', counts.mc_new_vnr_dup, 'amber', 'halb verwertet (MC profitiert)')}
        {cell('Beide Dublette', counts.both_dup, 'red', 'verschwendet')}
      </Box>
    </Paper>
  );
};

export default CoregMatrixWidget;
